import { useRef, useCallback, useEffect } from 'react';

/**
 * Hook para manejar listas paginadas con IntersectionObserver (scroll infinito)
 *
 * @param loading - Estado de carga actual
 * @param hasMore - Indica si hay más elementos por cargar
 * @param onLoadMore - Callback ejecutado cuando se debe cargar más elementos
 * @param options - Configuración adicional del observer
 * @returns ref para el último elemento y función de cleanup
 *
 * @example
 * const { lastElementRef } = usePaginatedList(loading, hasMore, () => {
 *   setOffset(prev => prev + ITEMS_BY_PAGE);
 * });
 *
 * // En el render:
 * <div ref={index === items.length - 1 ? lastElementRef : null}>
 *   <Item />
 * </div>
 */
interface UsePaginatedListOptions {
  threshold?: number;
  rootMargin?: string;
  enabled?: boolean;
}

interface UsePaginatedListReturn<T extends HTMLElement> {
  lastElementRef: (node: T | null) => void;
  resetObserver: () => void;
}

export function usePaginatedList<T extends HTMLElement = HTMLDivElement>(
  loading: boolean,
  hasMore: boolean,
  onLoadMore: () => void,
  options: UsePaginatedListOptions = {}
): UsePaginatedListReturn<T> {
  const {
    threshold = 1.0,
    rootMargin = '100px',
    enabled = true,
  } = options;

  const observer = useRef<IntersectionObserver | null>(null);

  const resetObserver = useCallback(() => {
    if (observer.current) {
      observer.current.disconnect();
      observer.current = null;
    }
  }, []);

  const lastElementRef = useCallback(
    (node: T | null) => {
      // Early returns para optimización
      if (loading) return;
      if (!enabled) return;
      if (!hasMore) return;

      // Limpiar observer anterior
      if (observer.current) {
        observer.current.disconnect();
      }

      // Si no hay nodo, salir
      if (!node) return;

      // Crear nuevo observer
      observer.current = new IntersectionObserver(
        (entries) => {
          const [entry] = entries;
          if (entry.isIntersecting && hasMore && !loading) {
            onLoadMore();
          }
        },
        {
          threshold,
          rootMargin,
        }
      );

      // Observar el nodo
      observer.current.observe(node);
    },
    [loading, hasMore, enabled, onLoadMore, threshold, rootMargin]
  );

  // Cleanup automático en unmount
  useEffect(() => {
    return () => {
      resetObserver();
    };
  }, [resetObserver]);

  return {
    lastElementRef,
    resetObserver,
  };
}

/**
 * Variante específica para paginación con offset
 * Simplifica el uso común de offset-based pagination
 *
 * @example
 * const { lastElementRef } = usePaginatedListWithOffset(
 *   loading,
 *   hasMore,
 *   offset,
 *   setOffset,
 *   ITEMS_BY_PAGE,
 *   { enabled: paginable }
 * );
 */
export function usePaginatedListWithOffset<T extends HTMLElement = HTMLDivElement>(
  loading: boolean,
  hasMore: boolean,
  offset: number,
  setOffset: React.Dispatch<React.SetStateAction<number>>,
  itemsPerPage: number,
  options: UsePaginatedListOptions = {}
): UsePaginatedListReturn<T> {
  const handleLoadMore = useCallback(() => {
    setOffset((prevOffset) => prevOffset + itemsPerPage);
  }, [setOffset, itemsPerPage]);

  return usePaginatedList(loading, hasMore, handleLoadMore, options);
}
